import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { bizApi, loadBiz, clearBiz, saveBiz } from "@/lib/bizApi";
import {
  MusicNotesSimple, Storefront, ArrowLeft, SignOut, WhatsappLogo, Copy,
  CheckCircle, Warning, UploadSimple, PlayCircle, Receipt, Buildings, Clock,
} from "@phosphor-icons/react";

export default function NegociosPanel() {
  const nav = useNavigate();
  const [biz, setBiz] = useState(loadBiz());
  const [me, setMe] = useState(null);
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!loadBiz()) { nav("/negocios/login"); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await bizApi.get("/business/me");
      setMe(data);
      saveBiz(localStorage.getItem("tm_biz_token"), { ...biz, ...data });
      setBiz(b => ({ ...b, ...data }));
    } catch (e) {
      if (e?.response?.status === 401) { clearBiz(); nav("/negocios/login"); }
    } finally { setLoading(false); }
  };

  const logout = () => { clearBiz(); nav("/"); };

  const startPayment = async () => {
    setLoading(true);
    try {
      const { data } = await bizApi.post("/business/payment/bbva");
      setTx(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error iniciando pago");
    } finally { setLoading(false); }
  };

  const uploadReceipt = async (file) => {
    if (!tx) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("La imagen debe ser menor a 8MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("transaction_id", tx.transaction_id);
      await bizApi.post("/business/payment/upload-receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("¡Comprobante enviado! El administrador validará tu pago.");
      setTx(t => ({ ...t, status: "pending_review", has_receipt: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo comprobante");
    } finally { setUploading(false); }
  };

  const copyCommerce = async () => {
    await navigator.clipboard.writeText(me.commerce_id);
    toast.success("Número de comercio copiado");
  };

  const playerUrl = me ? `${window.location.origin}/play/${me.commerce_id}` : "";

  const copyPlayerLink = async () => {
    await navigator.clipboard.writeText(playerUrl);
    toast.success("Link del reproductor copiado");
  };

  if (!biz || !me) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white/40">Cargando…</div>;
  }

  const isActive = me.subscription_status === "active";
  const statusColors = {
    active: { bg: "bg-[#B4F34A]/15", t: "text-[#B4F34A]", label: "Activa" },
    pending_payment: { bg: "bg-[#FFB800]/15", t: "text-[#FFB800]", label: "Esperando pago" },
    expired: { bg: "bg-[#FF4B00]/15", t: "text-[#FF4B00]", label: "Vencida" },
    suspended: { bg: "bg-[#FF4B00]/15", t: "text-[#FF4B00]", label: "Suspendida" },
  };
  const sc = statusColors[me.subscription_status] || statusColors.pending_payment;
  const validUntil = me.subscription_until ? new Date(me.subscription_until).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" }) : null;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="biz-panel-brand">
            <div className="w-8 h-8 rounded-full bg-[#FFB800] flex items-center justify-center">
              <MusicNotesSimple weight="fill" size={18} className="text-black" />
            </div>
            <span className="font-display font-bold">Tu MusiKa <span className="text-[#B4F34A] text-sm">Negocios</span></span>
          </Link>
          <button onClick={logout} className="text-xs text-white/60 hover:text-white flex items-center gap-1.5 px-3 py-2 rounded-full hover:bg-white/5" data-testid="biz-logout">
            <SignOut size={14} /> Cerrar sesión
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm">
          <ArrowLeft size={14} /> Inicio
        </Link>

        {/* Welcome card */}
        <div className="rounded-2xl bg-gradient-to-br from-[#0e1d10] to-[#0a0a0a] border border-[#B4F34A]/20 p-6 sm:p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[#B4F34A] flex items-center justify-center shrink-0">
              <Storefront weight="fill" size={28} className="text-black" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-[#B4F34A] mb-1">Negocio · {me.plan_info?.name?.replace("Plan ", "Plan ") || "Plan Básico"}</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold truncate">{me.name}</h1>
              <p className="text-sm text-white/50 mt-1">{me.address}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-black/40 border border-white/5 p-4">
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Tu número de comercio</p>
              <div className="flex items-center gap-2">
                <p className="font-display font-black text-2xl text-[#FFB800] tracking-wider" data-testid="biz-commerce-id">{me.commerce_id}</p>
                <button onClick={copyCommerce} className="ml-auto p-2 rounded-lg bg-white/5 hover:bg-white/10" title="Copiar" data-testid="biz-copy-commerce">
                  <Copy size={16} />
                </button>
              </div>
              <p className="text-xs text-white/40 mt-2">Este número es único y permanente.</p>
            </div>
            <div className="rounded-xl bg-black/40 border border-white/5 p-4">
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Suscripción</p>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full ${sc.bg} ${sc.t}`} data-testid="biz-status">
                  {sc.label}
                </span>
              </div>
              {validUntil && <p className="text-xs text-white/60 mt-2">Vigente hasta: <span className="text-white font-semibold">{validUntil}</span></p>}
            </div>
          </div>
        </div>

        {/* PAYMENT SECTION */}
        {!isActive && (
          <div className="rounded-2xl bg-[#121212] border border-white/5 p-6 sm:p-8" data-testid="biz-payment-section">
            <div className="flex items-center gap-2 mb-2">
              <Receipt size={22} weight="duotone" className="text-[#FFB800]" />
              <h2 className="font-display text-2xl font-bold">Activa tu suscripción</h2>
            </div>
            <p className="text-white/60 mb-2 text-sm sm:text-base">
              Plan contratado: <span className="text-white font-semibold">{me.plan_info?.name || me.plan}</span>
              {me.billing === "yearly" ? " · anual (con 20% descuento)" : " · mensual"}
              {me.locations > 1 ? ` · ${me.locations} sucursales` : ""}
            </p>
            <p className="text-white/60 mb-6 text-sm sm:text-base">
              Transfiere por BBVA <span className="text-[#FFB800] font-bold">${(me.price_mxn || 0).toLocaleString("es-MX")} MXN</span> y sube tu comprobante.
              Al validar, tu reproductor quedará activo por {me.billing === "yearly" ? "365" : "30"} días.
            </p>

            {!tx ? (
              <button
                onClick={startPayment}
                disabled={loading}
                className="px-6 py-3 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-semibold disabled:opacity-40"
                data-testid="biz-start-payment"
              >
                {loading ? "Cargando…" : `Iniciar pago BBVA $${(me.price_mxn || 0).toLocaleString("es-MX")} MXN`}
              </button>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl bg-black/40 p-5 border border-white/10 space-y-2 text-sm">
                  <Row label="Banco" value={tx.bank} />
                  <Row label="Beneficiario" value={tx.account_name} />
                  <Row label="CLABE" value={tx.clabe_or_account} mono />
                  <Row label="Monto" value={`$${tx.amount} MXN`} accent="#B4F34A" />
                  <Row label="Referencia" value={tx.reference} mono accent="#FFB800" />
                </div>
                <a
                  href={tx.whatsapp_link}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-3 rounded-full bg-[#25D366] hover:bg-[#1FB858] text-black font-semibold flex items-center justify-center gap-2"
                  data-testid="biz-report-whatsapp"
                >
                  <WhatsappLogo weight="fill" size={20} /> Reportar pago por WhatsApp
                </a>
                <div className="rounded-xl bg-white/5 p-4 border border-white/10">
                  <p className="text-sm font-semibold mb-2">2. Sube tu comprobante</p>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadReceipt(e.target.files[0])}
                    data-testid="biz-receipt-input"
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="w-full py-3 rounded-full bg-white/10 hover:bg-white/15 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
                    data-testid="biz-upload-receipt"
                  >
                    <UploadSimple size={18} weight="bold" />
                    {uploading ? "Subiendo…" : tx.has_receipt ? "Comprobante enviado · subir otro" : "Subir comprobante (foto)"}
                  </button>
                  {tx.has_receipt && (
                    <p className="text-xs text-[#B4F34A] mt-3 flex items-center gap-1">
                      <CheckCircle weight="fill" size={14} /> Comprobante recibido. Estamos validando tu pago.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CONNECTION INSTRUCTIONS (always visible) */}
        <div className="rounded-2xl bg-[#121212] border border-white/5 p-6 sm:p-8" data-testid="biz-instructions">
          <div className="flex items-center gap-2 mb-4">
            <PlayCircle size={22} weight="duotone" className="text-[#B4F34A]" />
            <h2 className="font-display text-2xl font-bold">Cómo conectar la música a tu equipo</h2>
          </div>
          {!isActive ? (
            <div className="rounded-xl bg-[#FFB800]/5 border border-[#FFB800]/20 p-4 mb-5 flex items-start gap-2">
              <Warning size={18} weight="fill" className="text-[#FFB800] shrink-0 mt-0.5" />
              <p className="text-sm text-white/80">
                Tu reproductor quedará activo en cuanto confirmemos tu pago. Mientras tanto, puedes prepararlo siguiendo los pasos:
              </p>
            </div>
          ) : (
            <div className="rounded-xl bg-[#B4F34A]/5 border border-[#B4F34A]/20 p-4 mb-5 flex items-start gap-2">
              <CheckCircle size={18} weight="fill" className="text-[#B4F34A] shrink-0 mt-0.5" />
              <div className="text-sm text-white/80">
                <p className="font-semibold text-[#B4F34A]">¡Tu reproductor está activo!</p>
                <p className="mt-1">Abre el siguiente enlace en cualquier tablet, laptop o PC conectada a tu equipo de sonido.</p>
              </div>
            </div>
          )}

          {/* Player link card */}
          <div className="rounded-xl bg-black/40 border border-white/10 p-4 mb-5">
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Tu reproductor exclusivo</p>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <p className="font-mono text-sm text-[#B4F34A] break-all" data-testid="biz-player-url">{playerUrl}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <a
                href={playerUrl}
                target="_blank"
                rel="noreferrer"
                className={`px-4 py-2 rounded-full text-sm font-semibold ${isActive ? "bg-[#B4F34A] hover:bg-[#A0E03A] text-black" : "bg-white/10 text-white/40 cursor-not-allowed"}`}
                onClick={(e) => { if (!isActive) e.preventDefault(); }}
                data-testid="biz-open-player"
              >
                <PlayCircle weight="fill" size={14} className="inline mr-1" /> Abrir reproductor
              </a>
              <button onClick={copyPlayerLink} className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 text-sm flex items-center gap-1.5">
                <Copy size={14} /> Copiar enlace
              </button>
            </div>
          </div>

          {/* Step-by-step */}
          <ol className="space-y-4">
            <Step n="1" title="Conecta tu equipo de sonido a una tablet, laptop o PC">
              Tu bocina o equipo de música debe estar conectado a un dispositivo con navegador (Chrome, Safari, Edge). Puede ser por cable auxiliar (3.5mm o RCA), Bluetooth, o conectándolo directamente vía HDMI/AUX a tu mezcladora.
            </Step>
            <Step n="2" title="Abre el reproductor en ese dispositivo">
              Copia el enlace de arriba y ábrelo en el navegador del equipo. Esa será la primera vez que reproduzcas — automáticamente quedará <span className="text-[#FFB800]">autorizado solo para esa red WiFi</span>.
            </Step>
            <Step n="3" title="Activa el modo pantalla completa y deja la música corriendo">
              El reproductor toca en modo aleatorio toda nuestra biblioteca, sin interrupciones, sin anuncios. Si cierras el navegador, simplemente vuélvelo a abrir.
            </Step>
            <Step n="4" title="Renueva tu suscripción cada periodo">
              Vamos a recordarte por WhatsApp 3 días antes de tu vencimiento. Vuelves aquí, repites el pago de tu plan y subes tu nuevo comprobante.
            </Step>
          </ol>

          {/* Anti-fraud notice */}
          <div className="mt-6 rounded-xl bg-[#FF4B00]/5 border border-[#FF4B00]/20 p-4">
            <p className="text-sm font-semibold text-[#FF4B00] mb-1 flex items-center gap-1.5">
              <Warning size={16} weight="fill" /> Importante: protección anti-fraude
            </p>
            <p className="text-xs text-white/70 leading-relaxed">
              Tu número de comercio queda asociado a la red WiFi de tu negocio desde la primera reproducción.
              Si alguien intenta usarlo desde otro lugar, el sistema lo bloquea automáticamente y nos avisa.
              Si cambias de ubicación legítimamente, contáctanos por WhatsApp para liberar el bloqueo.
            </p>
          </div>
        </div>

        {/* SCHEDULE (Pro / Premium only) — visible even before payment so they can configure ahead */}
        {me.plan_info?.allow_schedule && (
          <ScheduleSection biz={me} onSaved={refresh} />
        )}

        {/* What you get */}
        <div className="rounded-2xl bg-[#121212] border border-white/5 p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Buildings size={22} weight="duotone" className="text-[#FFB800]" />
            <h2 className="font-display text-2xl font-bold">Lo que incluye tu suscripción</h2>
          </div>
          <ul className="space-y-3 text-sm">
            {[
              "Acceso ilimitado a todo el catálogo: baladas, cumbias, rancheras, corridos, rock, salsa, reggaeton, banda y más",
              "Música original creada por artistas mexicanos y latinos — sin riesgo de multas por derechos de autor",
              "Reproductor en modo aleatorio que toca toda la noche sin interrupciones",
              "Sin anuncios, sin pausas, sin compromiso a largo plazo",
              "Apoyas directamente a creadores latinos",
              "Renovación mensual flexible — cancelas cuando quieras",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3">
                <CheckCircle weight="fill" size={18} className="text-[#B4F34A] shrink-0 mt-0.5" />
                <span className="text-white/80">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, accent }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-white/50">{label}</span>
      <span className={`${mono ? "font-mono" : "font-semibold"}`} style={{ color: accent || "#fff" }}>{value}</span>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="flex gap-4">
      <div className="w-9 h-9 rounded-full bg-[#B4F34A]/10 border border-[#B4F34A]/30 flex items-center justify-center shrink-0">
        <span className="font-display font-bold text-[#B4F34A]">{n}</span>
      </div>
      <div className="min-w-0">
        <p className="font-semibold mb-1">{title}</p>
        <p className="text-sm text-white/60 leading-relaxed">{children}</p>
      </div>
    </li>
  );
}

function ScheduleSection({ biz, onSaved }) {
  // Use a stable client-side _id per band so React keys survive reorder/remove
  const seedBands = () => {
    const base = biz.schedule && biz.schedule.length > 0 ? biz.schedule : [
      { start_hour: 8, end_hour: 14, mood: "tranquilo" },
      { start_hour: 14, end_hour: 20, mood: "movido" },
      { start_hour: 20, end_hour: 24, mood: "fiestero" },
    ];
    return base.map(b => ({ ...b, _id: b._id || `b-${Math.random().toString(36).slice(2, 9)}` }));
  };
  const [bands, setBands] = useState(seedBands);
  const [saving, setSaving] = useState(false);

  const MOODS = ["tranquilo", "romantico", "movido", "fiestero"];

  const updateBand = (id, key, value) => {
    setBands(bs => bs.map(b => (b._id === id ? { ...b, [key]: key === "mood" ? value : Number(value) } : b)));
  };
  const addBand = () => setBands(bs => [...bs, { start_hour: 0, end_hour: 6, mood: "tranquilo", _id: `b-${Math.random().toString(36).slice(2, 9)}` }]);
  const removeBand = (id) => setBands(bs => bs.filter(b => b._id !== id));

  const save = async () => {
    setSaving(true);
    try {
      // Strip client-only _id before sending to backend
      const payload = bands.map(({ _id, ...rest }) => rest); // eslint-disable-line no-unused-vars
      await bizApi.post("/business/schedule", { bands: payload });
      toast.success("Horario guardado");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando horario");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl bg-[#121212] border border-white/5 p-6 sm:p-8" data-testid="biz-schedule">
      <div className="flex items-center gap-2 mb-1">
        <Clock weight="duotone" size={22} className="text-[#FFB800]" />
        <h2 className="font-display text-2xl font-bold">Programación por horarios</h2>
      </div>
      <p className="text-sm text-white/60 mb-5">
        Define qué tipo de música suena en cada momento del día. Música tranquila en la mañana, movida en la noche.
      </p>

      <div className="space-y-3">
        {bands.map((b, i) => (
          <div key={b._id} className="rounded-xl bg-black/40 border border-white/10 p-3 flex flex-wrap items-center gap-2" data-testid={`schedule-band-${i}`}>
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/40">De</span>
              <input
                type="number" min="0" max="23"
                value={b.start_hour}
                onChange={(e) => updateBand(b._id, "start_hour", e.target.value)}
                className="w-16 px-2 py-1.5 rounded-lg bg-[#121212] border border-white/10 text-white text-center"
              />
              <span className="text-xs text-white/40">a</span>
              <input
                type="number" min="1" max="24"
                value={b.end_hour}
                onChange={(e) => updateBand(b._id, "end_hour", e.target.value)}
                className="w-16 px-2 py-1.5 rounded-lg bg-[#121212] border border-white/10 text-white text-center"
              />
              <span className="text-xs text-white/40">hrs</span>
            </div>
            <div className="flex gap-1 ml-auto flex-wrap">
              {MOODS.map(m => (
                <button
                  key={m}
                  onClick={() => updateBand(b._id, "mood", m)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${b.mood === m ? "bg-[#FFB800] text-black" : "bg-white/5 hover:bg-white/10 text-white/70"}`}
                  data-testid={`band-${i}-mood-${m}`}
                >{m}</button>
              ))}
              <button onClick={() => removeBand(b._id)} className="px-2 py-1.5 rounded-full bg-[#FF4B00]/10 hover:bg-[#FF4B00]/20 text-[#FF4B00] text-xs">×</button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mt-4 flex-wrap">
        <button onClick={addBand} className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 text-sm" data-testid="biz-schedule-add">
          + Agregar banda
        </button>
        <button onClick={save} disabled={saving} className="ml-auto px-5 py-2 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] text-black font-bold text-sm disabled:opacity-40" data-testid="biz-schedule-save">
          {saving ? "Guardando…" : "Guardar horario"}
        </button>
      </div>
    </div>
  );
}
